// @ts-nocheck

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider, CircularProgress, Chip, Autocomplete } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import AtomSlider from '@/components/atoms/Slider';
import AtomTextField from '@/components/atoms/TextField';
import segmentStyles from '@/pages/Segments/segments.module.css';
import { AppDispatch } from '@/store';
import {
  selectCompanySegments,
  selectStudyYearCompaniesLoading,
  selectStudyYearCompaniesUpdating,
  selectStudyYearCompaniesError,
} from '@/store/study-year-companies/selectors';
import {
  selectCategoryCompanies,
  selectStudyYearCategoriesLoading,
  selectChildCategories,
} from '@/store/study-year-categories/selectors';
import { studyYearCompanies } from '@/store/study-year-companies/thunks';
import { studyYearCategories } from '@/store/study-year-categories/thunks';

interface SegmentItem {
  id: string;
  category_name: string;
  concentration: number;
  status: string;
  analysis: string;
  note: string;
}

interface ParentSegment {
  id: string;
  study_year_category_id: string;
  category_name: string;
  description?: string;
  has_children: boolean;
}

interface ChildSegment {
  id: string;
  study_year_category_company_id: string;
  category_name: string;
  concentration: number;
  status: string;
  analysis: string;
  note: string;
}

interface SegmentsDialogContentProps {
  selectedRow: {
    study_year_company_id: string;
    company_name: string;
    [key: string]: any;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const SegmentsDialogContent: React.FC<SegmentsDialogContentProps> = ({
  selectedRow,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const currentUser = useSelector((state: any) => state.auth?.user);
  const companySegments = useSelector(selectCompanySegments);
  const categoryCompanies = useSelector(selectCategoryCompanies);
  const childCategories = useSelector(selectChildCategories);
  const isLoading = useSelector(selectStudyYearCompaniesLoading);
  const isCategoriesLoading = useSelector(selectStudyYearCategoriesLoading);
  const isUpdating = useSelector(selectStudyYearCompaniesUpdating);
  const error = useSelector(selectStudyYearCompaniesError);

  // Local state
  const [parentSegments, setParentSegments] = useState<ParentSegment[]>([]);
  const [selectedParentSegment, setSelectedParentSegment] = useState<ParentSegment | null>(null);
  const [childSegments, setChildSegments] = useState<ChildSegment[]>([]);
  const [segments, setSegments] = useState<SegmentItem[]>([]);
  const [localError, setLocalError] = useState('');

  // Fetch initial company segments data when component mounts
  useEffect(() => {
    if (selectedRow?.study_year_company_id) {
      dispatch(
        studyYearCompanies.getStudyYearCompanySegments({
          studyYearCompanyId: selectedRow.study_year_company_id,
        })
      );
    }
  }, [selectedRow?.study_year_company_id, dispatch]);

  // Extract parent segments from company segments data
  useEffect(() => {
    if (companySegments && Array.isArray(companySegments)) {
      console.log(companySegments);
      // Just use the data as-is, no manipulation
      setParentSegments(companySegments);
    }
  }, [companySegments]);

  // Handle parent segment selection
  const handleParentSegmentChange = (
    event: React.SyntheticEvent,
    newValue: ParentSegment | null
  ) => {
    setSelectedParentSegment(newValue);
    setChildSegments([]);
    setSegments([]);
    setLocalError('');

    // Add back the dispatch for fetching children
    if (newValue?.study_year_category_id) {
      dispatch(
        studyYearCategories.getChildCategories({
          studyYearCategoryId: newValue.study_year_category_id,
        })
      );
    }
  };

  useEffect(() => {
    if (childCategories && Array.isArray(childCategories) && selectedParentSegment) {
      // Use the child categories data as-is, no manipulation
      setChildSegments(childCategories);
      setSegments(childCategories);
    }
  }, [childCategories, selectedParentSegment]);

  // Handle Redux errors
  useEffect(() => {
    if (error) {
      console.error('Company Segments Error:', error);
      setLocalError(error);
    }
  }, [error]);

  // Calculate total concentration for validation
  const totalConcentration = useMemo(
    () => segments.reduce((sum, item) => sum + item.concentration, 0),
    [segments]
  );

  // Handle concentration slider change
  const handleConcentrationSliderChange = (
    index: number,
    _: Event,
    newValue: number | number[]
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    const newConcentration = value / 100; // Convert percentage to decimal

    // Calculate total of other concentrations
    const totalOtherConcentrations = segments.reduce(
      (sum, item, i) => (i !== index ? sum + item.concentration : sum),
      0
    );

    // Validate that total doesn't exceed 100%
    if (totalOtherConcentrations + newConcentration > 1) {
      setLocalError('Total concentration cannot exceed 100%');
      return;
    }

    const newSegments = [...segments];
    newSegments[index] = {
      ...newSegments[index],
      concentration: newConcentration,
    };

    setLocalError('');
    setSegments(newSegments);
  };

  // Handle direct concentration input change
  const handleConcentrationInputChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    const newConcentrationPercent = Number(newValue);

    if (isNaN(newConcentrationPercent)) return;

    const newConcentration = newConcentrationPercent / 100;

    // Calculate total of other concentrations
    const totalOtherConcentrations = segments.reduce(
      (sum, item, i) => (i !== index ? sum + item.concentration : sum),
      0
    );

    if (totalOtherConcentrations + newConcentration > 1) {
      setLocalError(`Total concentration cannot exceed 100%`);
      return;
    }

    const newSegments = [...segments];
    newSegments[index] = {
      ...newSegments[index],
      concentration: newConcentration,
    };

    setLocalError('');
    setSegments(newSegments);
  };

  // Submit the updated segments
  const submitSegmentUpdates = async () => {
    try {
      // Validate total concentration
      const total = segments.reduce((sum, item) => sum + item.concentration, 0);
      if (Math.abs(total - 1) > 0.01) {
        setLocalError(
          `Total concentration must equal 100%. Currently: ${(total * 100).toFixed(2)}%`
        );
        return;
      }

      // Prepare update payload with child segment data
      const updatePayload = {
        studyYearCompanyId: selectedRow.study_year_company_id,
        user_id: currentUser?.id,
        parentSegmentId: selectedParentSegment?.study_year_category_id,
        segments: segments.map((item, index) => ({
          id: item.id,
          study_year_category_company_id: childSegments[index]?.study_year_category_company_id,
          concentration: item.concentration,
          status: item.status,
          analysis: item.analysis,
          note: item.note,
        })),
      };

      // Replace this with your actual update thunk
      // await dispatch(studyYearCompanies.updateStudyYearCompanySegments(updatePayload));

      console.log('Update payload:', updatePayload);

      // For now, just simulate success
      setLocalError('');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating segments:', err);
      setLocalError('Failed to update segments. Please try again.');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading segments data...</Typography>
      </Box>
    );
  }

  return (
    <div style={{ minHeight: '400px' }}>
      <h3>Edit Company Segments</h3>

      {/* Summary Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 2,
          bgcolor: '#f8f9fa',
          borderRadius: 1,
          border: '1px solid #e3f2fd',
        }}
      >
        <Box>
          <Typography variant="h6">Market Concentration Distribution</Typography>
          <Typography variant="body2" color="text.secondary">
            Company: {selectedRow?.company_name}
          </Typography>
        </Box>

        {selectedParentSegment && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h6"
              color={Math.abs(totalConcentration - 1) < 0.01 ? 'success.main' : 'error.main'}
            >
              Total: {(totalConcentration * 100).toFixed(2)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Must equal 100%
            </Typography>
          </Box>
        )}
      </Box>

      {/* Debug Section - Remove this in production */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f0f0f0', borderRadius: 1 }}>
          <Typography variant="caption" display="block">
            <strong>Debug Info:</strong>
          </Typography>
          <Typography variant="caption" display="block">
            Company Segments Length: {companySegments?.length || 0}
          </Typography>
          <Typography variant="caption" display="block">
            Parent Segments Length: {parentSegments.length}
          </Typography>
          <Typography variant="caption" display="block">
            Category Companies Length: {categoryCompanies?.length || 0}
          </Typography>
          <Typography variant="caption" display="block">
            Child Categories Length: {childCategories?.length || 0}
          </Typography>
          {childCategories?.length > 0 && (
            <details>
              <summary>Raw Child Categories Data (first item)</summary>
              <pre style={{ fontSize: '10px' }}>{JSON.stringify(childCategories[0], null, 2)}</pre>
            </details>
          )}
          {companySegments?.length > 0 && (
            <details>
              <summary>Raw Company Segments Data (first item)</summary>
              <pre style={{ fontSize: '10px' }}>{JSON.stringify(companySegments[0], null, 2)}</pre>
            </details>
          )}
          {categoryCompanies?.length > 0 && (
            <details>
              <summary>Raw Category Companies Data (first item)</summary>
              <pre style={{ fontSize: '10px' }}>
                {JSON.stringify(categoryCompanies[0], null, 2)}
              </pre>
            </details>
          )}
        </Box>
      )}

      {/* Parent Segment Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Parent Segment
        </Typography>

        <Autocomplete
          value={selectedParentSegment}
          onChange={handleParentSegmentChange}
          options={parentSegments}
          getOptionLabel={option => option.category_name}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Box>
                  <Typography variant="body1">{option.category_name}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }}
          renderInput={params => (
            <AtomTextField
              {...params}
              label="Parent Segment"
              placeholder="Select a parent segment to view children"
              variant="outlined"
              fullWidth
            />
          )}
          loading={isLoading}
          disabled={parentSegments.length === 0}
          sx={{ width: '100%' }}
        />

        {parentSegments.length === 0 && !isLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            No parent segments available for this company.
          </Typography>
        )}
      </Box>

      {/* Children Loading State */}
      {isCategoriesLoading && selectedParentSegment && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ mt: 1 }}>Loading child segments...</Typography>
        </Box>
      )}

      {/* Error Display */}
      {(localError || error) && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
          }}
        >
          {localError || error}
        </Box>
      )}

      {/* Child Segments List */}
      {selectedParentSegment && segments.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Child Segments for: {selectedParentSegment.category_name}
          </Typography>

          <div className={segmentStyles.concentrationDialogContent}>
            {segments.map((segment, index) => (
              <div key={segment.id} className={segmentStyles.concentrationField}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">{segment.category_name}</Typography>
                  <Chip
                    label={segment.status}
                    size="small"
                    color={segment.status === 'override' ? 'success' : 'default'}
                    variant="filled"
                  />
                </Box>

                {/* Concentration Slider */}
                <AtomSlider
                  label="Market Concentration"
                  value={Number((segment.concentration * 100).toFixed(2))}
                  color="primary"
                  size="small"
                  min={0}
                  max={100}
                  step={0.01}
                  onChange={(e, value) => handleConcentrationSliderChange(index, e, value)}
                  showValueLabel
                  customTooltip
                  tooltipContent={value => `${value.toFixed(2)}%`}
                  showInput={true}
                  inputPostfix="%"
                  sx={{
                    WebkitAppearance: 'none', // Use camelCase instead of kebab-case
                  }}
                />

                {/* Direct Input Field */}
                <Box sx={{ mt: 1 }}>
                  <AtomTextField
                    className={segmentStyles.textField}
                    variant="outlined"
                    label="Concentration (%)"
                    value={(segment.concentration * 100).toFixed(2)}
                    onChange={e => handleConcentrationInputChange(index, e)}
                    inputProps={{
                      step: 0.01,
                      min: 0,
                      max: 100,
                      type: 'number',
                    }}
                    sx={{ width: '200px' }}
                  />
                </Box>

                {/* Additional Info */}
                {(segment.note || segment.analysis) && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {segment.note && (
                      <Typography variant="caption" display="block">
                        <strong>Note:</strong> {segment.note}
                      </Typography>
                    )}
                    {segment.analysis && (
                      <Typography variant="caption" display="block">
                        <strong>Analysis:</strong> {segment.analysis}
                      </Typography>
                    )}
                  </Box>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* No Parent Selected State */}
      {!selectedParentSegment && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            Please select a parent segment to view and edit child segments.
          </Typography>
        </Box>
      )}

      {/* No Children Found State */}
      {selectedParentSegment && segments.length === 0 && !isCategoriesLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            No child segments found for the selected parent segment.
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <AtomButton
          variant="outlined"
          color="primary"
          size="large"
          onClick={onClose}
          disabled={isUpdating || isCategoriesLoading}
        >
          Cancel
        </AtomButton>

        <AtomButton
          variant="contained"
          color="primary"
          size="large"
          onClick={submitSegmentUpdates}
          disabled={
            isUpdating ||
            isCategoriesLoading ||
            !selectedParentSegment ||
            segments.length === 0 ||
            Math.abs(totalConcentration - 1) > 0.01
          }
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </AtomButton>
      </Box>
    </div>
  );
};

export default SegmentsDialogContent;
